/**
 * Lookup search hook.
 *
 * Wraps GET /v1/admin/lookup as an on-demand MUTATION rather than a query:
 * a search is a command triggered by submit, not server state that should
 * refetch on focus/reconnect (a focused window re-running the operator's last
 * search would be surprising and pointless). No retry on validation errors
 * (an unrecognized query stays failed); transient failures are surfaced by the
 * page error state with the server request id.
 */
import { useMutation } from '@tanstack/react-query';
import { adminLookup } from '@/api/admin';
import type { AdminLookupResponse } from '@/api/admin-types';

export interface UseAdminLookupResult {
  /** Submit a trimmed query. Rejects with ApiError on failure. */
  search: (query: string) => Promise<AdminLookupResponse>;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /** The last successful response (a null match included). */
  data: AdminLookupResponse | undefined;
  reset: () => void;
}

export function useAdminLookup(): UseAdminLookupResult {
  const mutation = useMutation({
    mutationFn: (query: string) => adminLookup(query.trim()),
  });

  return {
    search: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
